import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import './i18n';
import { useTranslation } from 'react-i18next';
import { CartProvider } from './context/CartContext';
import { TransitionProvider, TransitionFader } from './context/TransitionContext';
import Home from './pages/Home';
import Shop from './pages/Shop';
import Kostenlos from './pages/Kostenlos';
import Installer from './pages/Installer';
import ProductDetail from './pages/ProductDetail';
import AllInOne from './pages/AllInOne';
import DigitalCard from './pages/DigitalCard';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import Widerruf from './pages/Widerruf';
import VertragWiderrufen from './pages/VertragWiderrufen';
import Abmelden from './pages/Abmelden';
import VertraegeKuendigen from './pages/VertraegeKuendigen';
import AGB from './pages/AGB';
import Support from './pages/Support';
import CustomerLibrary from './pages/CustomerLibrary';
import RunnerPage from './pages/games/RunnerPage';
import AetherDashboard from './pages/Aether/AetherDashboard';
import AetherAuthGate from './pages/Aether/AetherAuthGate';
import { AetherAuthProvider } from './pages/Aether/AetherAuthContext';
import CookieBanner from './components/CookieBanner';
import CartSidebar from './components/CartSidebar';
import GlobalEffects from './components/GlobalEffects';
import SmoothScroll from './components/SmoothScroll';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import NotFound from './pages/NotFound';
import './App.css';

// Heavy 3D game — code-split (three.js/drei/models) so it only downloads when
// the game is actually opened. The loading UI now lives in RunnerPage/GameLoader.

// 3D text studio — also three.js-heavy, so code-split it the same way.
// Prototyp-Welt: NUR im lokalen Dev-Server. In einem Produktions-Build ist
// NODE_ENV 'production', die Bedingung also fest false — die Route landet
// damit nie auf der oeffentlichen Seite.
const IS_DEV = process.env.NODE_ENV === 'development';
const WorldSandbox = lazy(() => import('./pages/world/WorldSandbox'));
const Bausteine = lazy(() => import('./pages/world/Bausteine'));
const StudioFallback = () => (
  <div style={{ position: 'fixed', inset: 0, background: '#050505', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F97316', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.3em', fontSize: '0.8rem' }}>
    LÄDT…
  </div>
);

// (ScrollToTop replaced by <SmoothScroll/> — it handles both Lenis init and the
// per-route scroll-to-top, route-aware so Aether/Builder/Games keep their own
// internal scrolling.)

// Component to handle language from URL and auto-detection
const LanguageHandler = ({ children }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const langInUrl = pathParts[0];
    
    // If URL starts with /en, set language to English
    if (langInUrl === 'en') {
      if (i18n.language !== 'en') {
        i18n.changeLanguage('en');
      }
    } else {
      // For all other URLs (German default)
      if (i18n.language !== 'de') {
        i18n.changeLanguage('de');
      }
    }
    // <html lang> folgt der Route (index.html liefert fest "de"): wichtig für
    // Screenreader, Übersetzungsangebote und die Silbentrennung (hyphens: auto).
    document.documentElement.lang = langInUrl === 'en' ? 'en' : 'de';
  }, [location.pathname, i18n]);

  return children;
};

// Component to handle subdomain redirect
const SubdomainHandler = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname.startsWith('shop.') && location.pathname === '/') {
      navigate('/shop', { replace: true });
    }
  }, [navigate, location.pathname]);

  return children;
};

// Animated Routes wrapper
const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* German routes (default) */}
        <Route path="/" element={<Home />} />
        <Route path="/installer" element={<Installer />} />
        <Route path="/kostenlos" element={<Kostenlos />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/all-in-one" element={<AllInOne />} />
        <Route path="/shop/:productId" element={<ProductDetail />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/datenschutz" element={<Datenschutz />} />
        <Route path="/widerruf" element={<Widerruf />} />
        {/* Elektronische Widerrufsfunktion (§ 356a BGB), verlinkt aus dem Footer */}
        <Route path="/vertrag-widerrufen" element={<VertragWiderrufen />} />
        <Route path="/abmelden" element={<Abmelden />} />
        {/* Kündigungsbutton (§ 312k BGB) für das All-in-One-Abo, verlinkt aus dem Footer */}
        <Route path="/vertraege-kuendigen" element={<VertraegeKuendigen />} />
        <Route path="/agb" element={<AGB />} />
        <Route path="/support" element={<Support />} />
        <Route
          path="/konto/lizenzen"
          element={
            <AetherAuthProvider>
              <CustomerLibrary />
            </AetherAuthProvider>
          }
        />
        <Route path="/aether" element={<AetherAuthGate><AetherDashboard /></AetherAuthGate>} />
        {/* Shareable digital business card (davidmedia.de/card) */}
        <Route path="/card" element={<DigitalCard />} />
        <Route path="/visitenkarte" element={<DigitalCard />} />
        <Route path="/karte" element={<DigitalCard />} />
        <Route path="/games" element={<RunnerPage />} />
        <Route path="/games/runner" element={<RunnerPage />} />
        {IS_DEV && <Route path="/welt" element={<Suspense fallback={<StudioFallback />}><WorldSandbox /></Suspense>} />}
        {IS_DEV && <Route path="/bausteine" element={<Suspense fallback={<StudioFallback />}><Bausteine /></Suspense>} />}
        
        {/* English routes */}
        <Route path="/en" element={<Home />} />
        <Route path="/en/installer" element={<Installer />} />
        <Route path="/en/free" element={<Kostenlos />} />
        <Route path="/en/shop" element={<Shop />} />
        <Route path="/en/shop/all-in-one" element={<AllInOne />} />
        <Route path="/en/shop/:productId" element={<ProductDetail />} />
        <Route path="/en/imprint" element={<Impressum />} />
        <Route path="/en/privacy" element={<Datenschutz />} />
        <Route path="/en/withdrawal" element={<Widerruf />} />
        <Route path="/en/withdraw" element={<VertragWiderrufen />} />
        <Route path="/en/unsubscribe" element={<Abmelden />} />
        <Route path="/en/cancel-contract" element={<VertraegeKuendigen />} />
        <Route path="/en/terms" element={<AGB />} />
        <Route path="/en/support" element={<Support />} />
        <Route
          path="/en/account/library"
          element={
            <AetherAuthProvider>
              <CustomerLibrary />
            </AetherAuthProvider>
          }
        />
        <Route path="/en/games" element={<RunnerPage />} />
        <Route path="/en/games/runner" element={<RunnerPage />} />
        
        {/* Entfernte Seiten (Kontakt, Team, Portfolio, alte Testseiten, /shop/all): alte Links und
            Suchergebnisse landen auf der passenden neuen Seite statt auf einer 404 */}
        <Route path="/kontakt" element={<Navigate to="/support" replace />} />
        <Route path="/en/contact" element={<Navigate to="/en/support" replace />} />
        {['/team', '/portfolio', '/portfolio/:slug', '/new', '/aether-home', '/builder'].map(p => <Route key={p} path={p} element={<Navigate to="/" replace />} />)}
        {['/en/team', '/en/portfolio', '/en/portfolio/:slug', '/en/builder'].map(p => <Route key={p} path={p} element={<Navigate to="/en" replace />} />)}
        <Route path="/shop/all" element={<Navigate to="/shop" replace />} />
        <Route path="/en/shop/all" element={<Navigate to="/en/shop" replace />} />

        {/* 404 — branded NotFound (was: silent redirect to home) */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  return (
    <div className="App">
      {/* ErrorBoundary catches uncaught render errors anywhere in the tree
          and shows a branded fallback instead of React's blank white page.
          Outside CartProvider so even a CartProvider crash is caught. */}
      <ErrorBoundary>
        {/* ToastProvider provides useToast() — replaces native alert/confirm
            with branded glass-style banners + a confirm dialog. */}
        <ToastProvider>
          <CartProvider>
            <BrowserRouter>
              {/* TransitionProvider needs to sit INSIDE BrowserRouter so
                  it can use useNavigate/useLocation, and OUTSIDE
                  AnimatedRoutes so the wipe overlay stays mounted
                  across route changes. */}
              <TransitionProvider>
                <SmoothScroll />
                <LanguageHandler>
                  <SubdomainHandler>
                    <GlobalEffects />
                    {/* TransitionFader only dims the page content while a
                        wipe is in flight; the cart sidebar / cookie banner
                        stay outside it so they don't appear to fade with
                        the route change. */}
                    <TransitionFader>
                      <AnimatedRoutes />
                    </TransitionFader>
                    <CookieBanner />
                    <CartSidebar />
                  </SubdomainHandler>
                </LanguageHandler>
              </TransitionProvider>
            </BrowserRouter>
          </CartProvider>
        </ToastProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
